import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiEdit2, 
  FiTrash2, 
  FiArrowLeft, 
  FiMail, 
  FiPhone,
  FiBriefcase,
  FiUser,
  FiStar,
  FiTrendingUp,
  FiTarget,
  FiMessageSquare,
  FiCalendar,
  FiAlertCircle
} from 'react-icons/fi';
import stakeholderService from '../../services/stakeholderService';

const StakeholderView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stakeholder, setStakeholder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStakeholder();
  }, [id]);

  const fetchStakeholder = async () => {
    try {
      setLoading(true);
      const data = await stakeholderService.getStakeholder(id);
      setStakeholder(data.data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to fetch stakeholder');
      navigate('/app/stakeholders');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this stakeholder?')) {
      return;
    }

    try {
      await stakeholderService.deleteStakeholder(id);
      toast.success('Stakeholder deleted successfully');
      navigate('/app/stakeholders');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete stakeholder');
    }
  };

  const getInfluenceBadgeColor = (level) => {
    const colors = {
      Low: 'bg-gray-100 text-gray-800',
      Medium: 'bg-blue-100 text-blue-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getInterestBadgeColor = (level) => {
    const colors = {
      Low: 'bg-gray-100 text-gray-800',
      Medium: 'bg-green-100 text-green-800',
      High: 'bg-yellow-100 text-yellow-800',
      Critical: 'bg-purple-100 text-purple-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      Active: 'bg-green-100 text-green-800',
      Inactive: 'bg-gray-100 text-gray-800',
      Pending: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading stakeholder...</div>
      </div>
    );
  }

  if (!stakeholder) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Stakeholder not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/stakeholders')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{stakeholder.name}</h1>
              {stakeholder.is_key_stakeholder && (
                <span className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  <FiStar /> Key Stakeholder
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">{stakeholder.role || 'Stakeholder'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/app/stakeholders/${id}/edit`}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiEdit2 /> Edit
          </Link>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <FiTrash2 /> Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <FiMail className="text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <a href={`mailto:${stakeholder.email}`} className="text-blue-600 hover:underline">
                    {stakeholder.email}
                  </a>
                </div>
              </div>
              {stakeholder.phone && (
                <div className="flex items-center gap-3">
                  <FiPhone className="text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <a href={`tel:${stakeholder.phone}`} className="text-blue-600 hover:underline">
                      {stakeholder.phone}
                    </a>
                  </div>
                </div>
              )}
              {stakeholder.company && (
                <div className="flex items-center gap-3">
                  <FiBriefcase className="text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Company</div>
                    <div className="text-gray-900">{stakeholder.company}</div>
                  </div>
                </div>
              )}
              {stakeholder.position && (
                <div className="flex items-center gap-3">
                  <FiUser className="text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-500">Position</div>
                    <div className="text-gray-900">{stakeholder.position}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Classification */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Classification</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Stakeholder Type</div>
                <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                  {stakeholder.stakeholder_type}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Status</div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(stakeholder.status)}`}>
                  {stakeholder.status}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Influence Level</div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getInfluenceBadgeColor(stakeholder.influence_level)}`}>
                  <FiTrendingUp className="inline mr-1" />
                  {stakeholder.influence_level}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Interest Level</div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getInterestBadgeColor(stakeholder.interest_level)}`}>
                  <FiTarget className="inline mr-1" />
                  {stakeholder.interest_level}
                </span>
              </div>
            </div>
          </div>

          {/* Communication Preferences */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiMessageSquare /> Communication Preferences
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-1">Frequency</div>
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-gray-400" />
                  <span className="text-gray-900">{stakeholder.communication_frequency}</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Preferred Method</div>
                <div className="flex items-center gap-2">
                  <FiMessageSquare className="text-gray-400" />
                  <span className="text-gray-900">{stakeholder.preferred_communication_method}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Engagement Strategy */}
          {stakeholder.engagement_strategy && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Engagement Strategy</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{stakeholder.engagement_strategy}</p>
            </div>
          )}

          {/* Expectations */}
          {stakeholder.expectations && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiTarget /> Expectations
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{stakeholder.expectations}</p>
            </div>
          )}

          {/* Concerns */}
          {stakeholder.concerns && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiAlertCircle /> Concerns
              </h2>
              <p className="text-gray-700 whitespace-pre-wrap">{stakeholder.concerns}</p>
            </div>
          )}

          {/* Notes */}
          {stakeholder.notes && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{stakeholder.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Project Information */}
          {stakeholder.project && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Project</h2>
              <Link
                to={`/app/projects/${stakeholder.project.id}`}
                className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="font-medium text-gray-900">{stakeholder.project.name}</div>
                {stakeholder.project.code && (
                  <div className="text-sm text-gray-500 mt-1">{stakeholder.project.code}</div>
                )}
              </Link>
            </div>
          )}

          {/* Organization Information */}
          {stakeholder.organization && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization</h2>
              <div className="space-y-2">
                <div>
                  <div className="text-sm text-gray-500">Name</div>
                  <div className="text-gray-900">{stakeholder.organization.name}</div>
                </div>
                {stakeholder.organization.code && (
                  <div>
                    <div className="text-sm text-gray-500">Code</div>
                    <div className="text-gray-900">{stakeholder.organization.code}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h2>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500">Created</div>
                <div className="text-gray-900">
                  {new Date(stakeholder.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Last Updated</div>
                <div className="text-gray-900">
                  {new Date(stakeholder.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StakeholderView;

